import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MenuItem } from "@mui/material";
import { Badge } from "react-bootstrap";
import Sidebar from "../../components/sidebar/Sidebar";
import LoadingSpinner from "../../components/LoadingSpinner";
import "../accounts/view/ViewAccountPage.css";
import { MUI_TABLE } from '../../utils/muiTableConfig';
import { Money, Username, useMask } from '../../utils/privacyMask';
import {
  RESELLER_SERVICES,
  getServiceTiers,
  getServiceColor,
  indexRates,
  rateForCredits,
} from "../../utils/resellerServices";

/* ── Stat card (same shape as the one on the account view) ─────────── */
function StatCard({ label, value, icon, accent, glow }) {
  return (
    <div className="view-stat-card" style={{ '--accent': accent, '--glow': glow }}>
      <div className="view-stat-label">{label}</div>
      <div className="view-stat-value">{value}</div>
      <i className={`bi ${icon} view-stat-icon`} />
    </div>
  );
}

/* ── Service badge ─────────────────────────────────────────────────── */
function ServiceBadge({ code }) {
  const color = getServiceColor(code);
  return (
    <Badge bg="" style={{
      background: `${color}22`, color, border: `1px solid ${color}66`,
      fontSize: '0.72rem', fontWeight: 600, marginRight: 4,
    }}>
      {code}
    </Badge>
  );
}

export default function ResellersPage() {
  const navigate = useNavigate();
  const { mask } = useMask();

  const [resellers, setResellers]       = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [rates, setRates]               = useState({});
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [accRes, txRes, rateRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/account`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/transaction`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/reseller-rates`, { headers }),
        ]);
        setResellers((accRes.data || []).filter(a => a.accountType === 'reseller'));
        setTransactions((txRes.data || []).filter(t => t.type === 'reseller-credits'));
        setRates(indexRates(rateRes.data));
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.error || err.response?.data?.message || "Failed to fetch resellers");
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  /* ── One row per reseller, with their purchases rolled up ── */
  const rows = useMemo(() => resellers.map(account => {
    const mine = transactions.filter(t => String(t.accountID) === String(account._id));

    const paid    = mine.reduce((s, t) => s + (t.revenue || 0), 0);
    const profit  = mine.reduce((s, t) => s + (t.profit  || 0), 0);
    const credits = mine.reduce((s, t) => s + (t.resellerCredits?.credits || 0), 0);
    const services = [...new Set(mine.map(t => t.resellerCredits?.service).filter(Boolean))];
    const lastPurchase = mine.reduce((latest, t) => {
      const d = Number(t.transactionDate);
      return Number.isFinite(d) && d > latest ? d : latest;
    }, 0);

    return {
      _id: account._id,
      username: account.username,
      email: account.email || null,
      services,
      credits,
      paid,
      profit,
      margin: paid > 0 ? (profit / paid) * 100 : null,
      purchases: mine.length,
      lastPurchase: lastPurchase || null,
    };
  }), [resellers, transactions]);

  const totals = useMemo(() => ({
    credits: rows.reduce((s, r) => s + r.credits, 0),
    paid:    rows.reduce((s, r) => s + r.paid, 0),
    profit:  rows.reduce((s, r) => s + r.profit, 0),
  }), [rows]);

  const avgMargin = totals.paid > 0 ? (totals.profit / totals.paid) * 100 : 0;

  const columns = useMemo(() => [
    {
      header: 'Reseller', accessorKey: 'username',
      Cell: ({ row }) => (
        <span style={{ fontWeight: 600 }}><Username value={row.original.username} /></span>
      ),
    },
    {
      header: 'Services Sold', accessorFn: r => r.services.join(', '), size: 140,
      Cell: ({ row }) => row.original.services.length === 0
        ? <span style={{ color: 'var(--color-subtle)' }}>—</span>
        : row.original.services.map(s => <ServiceBadge key={s} code={s} />),
    },
    {
      header: 'Credits', accessorKey: 'credits', size: 90,
      Cell: ({ cell }) => <span style={{ fontWeight: 600 }}>{mask(cell.getValue().toLocaleString(), 'number')}</span>,
    },
    {
      header: 'Total Paid', accessorKey: 'paid', size: 110,
      Cell: ({ cell }) => <span style={{ color: '#34d399', fontWeight: 600 }}><Money value={cell.getValue()} /></span>,
    },
    {
      header: 'Total Profit', accessorKey: 'profit', size: 110,
      Cell: ({ cell }) => <span style={{ color: '#38bdf8' }}><Money value={cell.getValue()} /></span>,
    },
    {
      header: 'Margin', accessorKey: 'margin', size: 90,
      Cell: ({ cell }) => {
        const v = cell.getValue();
        if (v == null) return <span style={{ color: 'var(--color-subtle)' }}>—</span>;
        const color = v >= 20 ? '#4ade80' : v >= 0 ? '#fbbf24' : '#f87171';
        return <span style={{ color, fontWeight: 600 }}>{mask(`${v.toFixed(1)}%`, 'number')}</span>;
      },
    },
    {
      header: 'Purchases', accessorKey: 'purchases', size: 90,
      Cell: ({ cell }) => <span style={{ color: 'var(--color-muted)' }}>{cell.getValue()}</span>,
    },
    {
      header: 'Last Purchase', accessorKey: 'lastPurchase', size: 120,
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return v
          ? <span style={{ color: 'var(--color-muted)' }}>{new Date(v).toLocaleDateString()}</span>
          : <span style={{ color: 'var(--color-subtle)' }}>Never</span>;
      },
    },
  ], [mask]);

  const table = useMaterialReactTable({
    columns,
    data: rows ?? [],
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: false,
    enableGlobalFilter: true,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: false,
      columnPinning: { left: ['mrt-row-expand', 'mrt-row-select'], right: ['mrt-row-actions'] },
    },
    renderRowActionMenuItems: ({ row }) => [
      <MenuItem key="view" onClick={() => navigate(`/account/view/${row.original._id}`)} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-eye" style={{ fontSize: '0.9rem', color: '#64748b' }} />View
      </MenuItem>,
      <MenuItem key="credits" onClick={() => navigate(`/reseller/credits/create?accountId=${row.original._id}`)} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-coin" style={{ fontSize: '0.9rem', color: '#64748b' }} />Log Purchase
      </MenuItem>,
      <MenuItem key="edit" onClick={() => navigate(`/account/edit/${row.original._id}`)} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-pencil-square" style={{ fontSize: '0.9rem', color: '#64748b' }} />Edit
      </MenuItem>,
    ],
    ...MUI_TABLE,
  });

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Resellers" />

      <div className="view-page">
        {error && (
          <div style={{ padding: '12px 16px', marginBottom: 20, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        )}

        <div className="view-stats">
          <StatCard label="Resellers"       value={rows.length}                                          icon="bi-person-badge-fill" accent="#a78bfa" glow="rgba(167,139,250,0.3)" />
          <StatCard label="Credits Sold"    value={mask(totals.credits.toLocaleString(), 'number')}      icon="bi-coin"              accent="#f59e0b" glow="rgba(245,158,11,0.3)" />
          <StatCard label="Reseller Revenue" value={mask(`€${totals.paid.toFixed(2)}`, 'money')}         icon="bi-cash-stack"        accent="#10b981" glow="rgba(16,185,129,0.3)" />
          <StatCard label="Reseller Profit" value={mask(`€${totals.profit.toFixed(2)}`, 'money')}        icon="bi-graph-up-arrow"    accent="#3b82f6" glow="rgba(59,130,246,0.3)" />
          <StatCard label="Avg Margin"      value={mask(`${avgMargin.toFixed(1)}%`, 'number')}           icon="bi-percent"           accent="#06b6d4" glow="rgba(6,182,212,0.3)" />
        </div>

        <div className="view-panel">
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-person-badge-fill" style={{ color: '#a78bfa' }} />
              All Resellers
            </h6>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="view-panel-count">{rows.length}</span>
              <Link to="/reseller/rates" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                borderRadius: 8, border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.08)',
                color: '#fbbf24', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none',
              }}>
                <i className="bi bi-sliders" /> Cost Rates
              </Link>
              <Link to="/reseller/credits/create" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                borderRadius: 8, border: '1px solid rgba(167,139,250,0.3)', background: 'rgba(167,139,250,0.1)',
                color: '#c4b5fd', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none',
              }}>
                <i className="bi bi-plus-lg" /> Log Credit Purchase
              </Link>
            </div>
          </div>
          <MaterialReactTable table={table} />
        </div>

        {/* ── What each service actually is ── */}
        <div className="view-panel">
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-diagram-3-fill" style={{ color: '#06b6d4' }} />
              Service Reference
            </h6>
          </div>
          <div style={{ padding: '4px 20px 18px' }}>
            {RESELLER_SERVICES.map(({ service, vendor }) => {
              const tiers = getServiceTiers(service);
              const baseRate = rateForCredits(rates[service], 0);
              return (
                <div key={service} style={{ padding: '14px 0', borderBottom: '1px solid var(--color-border, rgba(148,163,184,0.12))' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                    <ServiceBadge code={service} />
                    <span style={{ fontWeight: 700 }}>Service {service}</span>
                    <span style={{ color: 'var(--color-muted)' }}>· {vendor}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'var(--color-muted)' }}>
                      {baseRate != null
                        ? <>our base cost {mask(`€${baseRate.toFixed(2)}`, 'money')} / credit</>
                        : 'no rate card set'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {tiers.length === 0
                      ? <span style={{ color: 'var(--color-subtle)', fontSize: '0.8rem' }}>No tiers in the catalogue</span>
                      : tiers.map(t => (
                          <Badge key={`${t.brand}-${t.tier}`} bg="" style={{
                            background: t.color, color: '#fff', fontSize: '0.72rem', fontWeight: 600,
                          }}>
                            {t.brand}: {t.tier}
                          </Badge>
                        ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
